/**
*
*This utility is used for have the common feature functions which will use in
*the application like JWT, formatClass or others
* @class Utils
*/
import jwtDecode from 'jwt-decode';
import configObj from 'config';
import { parseDomain, topLevelDomains } from 'parse-domain';
import Constants from 'constants';
import { notify } from 'react-notify-toast';
import _ from 'lodash';
import moment from 'moment';
import configHelper from 'helper/Config/configHelper';
import uuidv1 from 'uuid/v1';
import { favicon, title, baseUrl, clientName, env } from '../../server/utils';
import Translations from '../../translation.json';
import axios from 'axios';
import { analysisPublish } from '@tgslabs/clevertap-analysis';
import analyticsEventsConfig from './analyticsEventConfig';


class Utils {

    setCookie = (name, value, seconds, domain = '', expiry = '') => {
        let expires = "; expires=" + expiry;
        if (seconds) {
            let date = new Date();
            date.setTime(date.getTime() + (seconds * 1000));
            expires = "; expires=" + date.toGMTString();
        }
        const { cookieDomain = 'technogramsolutions.com' } = this.getCookieDomain();
        let primaryDomain = "; domain=" + (domain || cookieDomain);
        document.cookie = name + "=" + value + expires + primaryDomain + "; path=/";
    }

    deepClone = (payload) => {
        return JSON.parse(JSON.stringify(payload));
    }

    /**
   * @description generic error displaying function
   * @param errors
   * @returns toast err msg
   */
    showErrorMsg = (errors = []) => {
        errors.map((error) => {
            const err = error.message || Constants.apiErrors[error.errCode];
            return this.errorToaster(err, 7000);
        });
    }

    errorToaster = (msg, timePeriod = 5000, msgType) => {
        if (msgType) {
            notify.show(msg, msgType, timePeriod);
        } else {
            notify.show(msg, 'error', timePeriod);
        }
    }

    hideErrorToaster = () => {
        notify.hide();
    }

    getQueryObjectFromUrl = (searchUrl) => {
        const searchUrlString = decodeURIComponent(searchUrl);
        return searchUrlString.replace('?', '').split('&').reduce((searchQuery, query) => {
            const splitQuery = query.split('='),
                queryKey = splitQuery[0],
                queryValue = splitQuery[1];
            return {
                ...searchQuery,
                [queryKey]: queryValue
            };
        }, {});
    };
    getQueryObjectFromUrlExact = (searchUrl) => {
        const searchUrlString = decodeURIComponent(searchUrl);
        return searchUrlString.replace('?', '').split('&').reduce((searchQuery, query) => {
            const splitQuery = query.split('='),
                queryKey = splitQuery[0],
                queryValue = splitQuery[1];
            const myString = query.substring(query.indexOf('=') + 1)
            return {
                ...searchQuery,
                [queryKey]: myString
            };
        }, {});
    };


    cookieExpiryTime = (token = "") => {
        const decodeJWT = jwtDecode(token.replace('Bearer ', '')),
            { exp = '' } = decodeJWT;
        return new Date((exp - 300) * 1000).toGMTString();
    }

    isMobile = () => {
        return 768 > document.documentElement.clientWidth;
    }

    log = (payload) => {
        return env === Constants.module.DEVELOPMENT
            ? window.console.log(payload) : null;
    }

    saveToCookie = (token, removeOld) => {
        const expires = this.cookieExpiryTime(token),
            { cookieDomain = 'technogramsolutions.com' } = this.getCookieDomain();
        this.setCookie('uuid', token, '', cookieDomain, expires);
        if (removeOld) {
            this.removeCookie(removeOld);
        }
    }

    removeCookie = (name) => {
        const { cookieDomain = 'technogramsolutions.com' } = this.getCookieDomain();
        this.setCookie(name, '', '', cookieDomain, new Date(-1).toGMTString());
    }

    getCookie = (name) => {
        const nameEQ = `${name}=`,
            ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i = i + 1) {
            let c = ca[i];
            while (' ' === c.charAt(0)) {
                c = c.substring(1, c.length);
            }
            if (0 === c.indexOf(nameEQ)) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    }

    /** Get the User obj from JWT token */
    getUserInfoFromJwt = (token = "") => {
        try {
            const jwtToken = token ? token : this.getJwtToken();
            const decodedJwtToken = jwtDecode(jwtToken.replace('Bearer ', ''));
            return JSON.parse(decodedJwtToken.sub);
        } catch (error) {
            return {};
        }
    }

    getJwtToken = (token = 'uuid') => {
        return this.getCookie(token);
    }

    /***This function scrolls to top of window*/
    scrollToTop = () => {
        return window.scrollTo(0, 0);
    }

    isByPassVerification = () => {
        const userObj = this.getUserInfoFromJwt();
        const { role = '', emulateUser = {} } = userObj;
        const { role: roleEmulatedRole = '' } = emulateUser;
        const { verificationByPassRoles = [] } = this.getConfig();
        return verificationByPassRoles.reduce((accum, byPassRole) => {
            return accum || (roleEmulatedRole == byPassRole || role == byPassRole);
        }, false);
    }

    /**
    * Create classNames string to base class if conditions in config object evaluate to be true.
    */
    classNames = (baseClass, config) => {
        const classNames = [baseClass];
        Object.keys(config).forEach((_key) => {
            if (Object.prototype.hasOwnProperty.call(config, _key)) {
                if (config[_key]) {
                    classNames.push(_key);
                }
            }
        });
        return classNames.join(' ').trim();
    };

    /**
     * @param {string} searchUrl
     * @return {object} queryKey and Value
     */
    extractKeysFromUrl = (searchUrl) => {
        const searchUrlString = decodeURIComponent(searchUrl);
        return searchUrlString.replace('?', '').split('&').reduce((searchQuery, query) => {
            const splitQuery = query.split('='),
                queryKey = splitQuery[0],
                queryValue = splitQuery[1];
            return {
                ...searchQuery,
                [queryKey]: queryValue
            };
        }, {});
    };

    /* to get queryKey and Value from url having hash */

    extractParamValue = (url, paramName) => {
        let match = url.match(new RegExp(paramName + '=([^&]+)'));
        return match ? match[1] : null;
    };

    getUrlFromRoomInfo = (rooms) => {
        const roomCount = rooms.length;
        let roomStayQualifier = '';
        for (let i = 0; i < roomCount; i++) {
            let adultCount = rooms[i].numberOfAdults,
                childCount = rooms[i].numberOfChild,
                ages = '';

            for (let j = 0; j < childCount; j++) {
                let tmpAge = rooms[i].childAge[j];
                if (tmpAge === -1) {
                    tmpAge = 1;
                }
                ages = `${ages}${(tmpAge === 0 ? 0 : tmpAge)}e`;
            }
            roomStayQualifier = `${roomStayQualifier}${adultCount}e${childCount}e${ages}`;
        }
        return roomStayQualifier;
    };

    convertToRoomsArray = (roomStayQualifier) => {
        const { defaultRoomInfo = {} } = this.getConfig();
        const { numberOfAdults = 1, numberOfChild = 0, childAge = [] } = defaultRoomInfo;
        if (!roomStayQualifier || roomStayQualifier.indexOf('e') === -1) {
            let roomsDetailArray = [];
            roomsDetailArray.push({
                numberOfAdults: numberOfAdults,
                numberOfChild: numberOfChild,
                childAge: childAge
            });
            return roomsDetailArray;
        }
        let roomStayCandidatesList = [],
            rmStayTokens = roomStayQualifier.split('e');

        for (let i = 0; i < rmStayTokens.length - 1;) {
            let adult = {},
                adultCount = parseInt(rmStayTokens[i++], 10);
            adult.numberOfAdults = adultCount;
            let childCount = parseInt(rmStayTokens[i++], 10);
            adult.numberOfChild = childCount;
            if (childCount > 0) {
                let childrenAges = [];
                for (let j = 0; j < childCount; j++) {
                    let childAge = parseInt(rmStayTokens[i++], 10);
                    childrenAges.push(childAge);
                }
                adult.childAge = childrenAges;
            }
            roomStayCandidatesList.push(adult);
        }
        return roomStayCandidatesList;
    };

    getRatingsArray = (rating) => {
        if (_.isEmpty(rating)) {
            return null;
        }
        return rating.split(",").map(data => parseInt(data, 10));
    }

    isEmpty = (obj) => {
        if (_.isUndefined(obj) || obj == 0 || obj == '0'
            || obj === false || obj == null) {
            return true;
        }
        if (_.isObject(obj) && _.size(obj) === 0) {
            return true;
        }
        if (_.isNumber(obj) || _.isBoolean(obj) || _.isDate(obj)
            || (_.isObject(obj) && _.size(obj) > 0)) {
            return false;
        }
        return _.isEmpty(obj);
    };

    validateCheckInCheckout = (data) => {
        const { checkinDate = '', checkoutDate = '' } = data;
        if (!checkinDate || !checkoutDate)
            return false;

        const inDate = moment(checkinDate, 'YYYY-MM-DD'),
            outDate = moment(checkoutDate, 'YYYY-MM-DD');
        if (outDate.diff(inDate, 'days') < 1) {
            return false;
        }
        return true;
    };

    getLastIdFromUrl = () => {
        const url = window.location.pathname;
        return url.substring(url.lastIndexOf('/') + 1);
    };

    getLastIdFromUrlAsObj = () => {
        const [key, val] = utils.getLastIdFromUrl().split('=');
        return {
            [key]: val
        };
    };

    checkAgentUsingCorporatePan = (number = '', userPanNumber = '') => {
        if (number == userPanNumber) {
            return false
        }
        return true
    }

    formatPrice = (number, curr = 'INR', noStyle) => {
        let _formatted = '';
        if (noStyle) {
            _formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(number);
        } else {
            _formatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: curr, maximumFractionDigits: 2 }).format(number);
        }
        return isNaN(_formatted) ? 0 : _formatted;
    };

    formatDate = (date, type = 'lll') => {
        return moment(date).format(type);
    };

    dateDiff = (d1, d2) => {
        if (!d1 || !d2)
            return '-';

        const date2 = new Date(d1),
            date1 = new Date(d2),
            timeDiff = Math.abs(date2.getTime() - date1.getTime());
        return Math.ceil(timeDiff / (1000 * 3600 * 24));
    };

    // to calculate distance from  lat long
    distance = (lat1, lon1, lat2, lon2) => {
        const deg2rad = (deg) => {
            return deg * (Math.PI / 180)
        }
        const R = 6371; // Radius of the earth in km
        const dLat = deg2rad(lat2 - lat1);  // deg2rad below
        const dLon = deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
            ;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c; // Distance in km
        return isNaN(d) ? "0" : `${parseFloat((d).toFixed(2))}`;
    }

    /*======ProperCase =========*/
    lowerCase = (str) => {
        return str.toLowerCase();
    };

    upperCase = (str) => {
        return str.toUpperCase();
    };

    properCase = (str = '') => {
        const removeUnderscore = str.replace(/(\w)(_)(\w)/g, '$1 $3');
        return utils.lowerCase(removeUnderscore).replace(/^\w|\s\w/g, utils.upperCase);
    };

    checkValidAmount = val => {
        return val !== '' ? val : 0;
    };

    getObejctDifference = (newObj, oldObj) => {
        return _.reduce(newObj, (result, value, key) => {
            const finalResult = result;
            if (!_.isEqual(value, oldObj[key])) {
                if (_.isArray(value)) {
                    finalResult[key] = [];
                    _.forEach(value, (innerObjFrom1, index) => {
                        if (_.isNil(oldObj[key][index])) {
                            finalResult[key].push(innerObjFrom1);
                        } else {
                            let changes = this.getObejctDifference(innerObjFrom1, oldObj[key][index]);
                            if (!_.isEmpty(changes)) {
                                finalResult[key].push(changes);
                            }
                        }
                    });
                } else if (_.isObject(value)) {
                    finalResult[key] = this.getObejctDifference(value, oldObj[key]);
                } else {
                    finalResult[key] = value;
                }
            }
            return finalResult;
        }, {});
    };

    getSpecificArrObj = (arr = [], compVal, condition) => {
        return arr.filter(obj => obj[compVal] === condition);
    };

    formatPrice = (number, curr, noStyle) => {
        if (noStyle)
            return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(number);
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: curr ? curr : 'INR', maximumFractionDigits: 2 }).format(number);
    };

    formatPriceWithoutDecimal = (number, curr, noStyle) => {
        if (noStyle) {
            return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(number);
        }
        return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(number);
    };

    removeHTMLTags = (str = '') => {
        return str.replace(/<[^>]*>/g, '');
    };

    /**
     * @description give comma seperated values
     * @param {num} length of array
     * @param {num} current index
     * @param {string} value
     * @returns comma seperated values
     */
    getComma = (arrLength, currIndex, val) => {
        return arrLength !== currIndex + 1 ? `${val}, ` : val;
    };

    removeEmptyKeysFromObject = (obj) => {
        Object.keys(obj).forEach(key => {
            if (Object.prototype.toString.call(obj[key]) === '[object Date]' && (obj[key].toString().length === 0 || obj[key].toString() === 'Invalid Date')) {
                delete obj[key];
            } else if (obj[key] && typeof obj[key] === 'object') {
                utils.removeEmptyKeysFromObject(obj[key]);
            } else if (obj[key] == null || obj[key] === '') {
                delete obj[key];
            }

            if (obj[key]
                && typeof obj[key] === 'object'
                && Object.keys(obj[key]).length === 0
                && Object.prototype.toString.call(obj[key]) !== '[object Date]') {
                delete obj[key];
            }
        });
        return obj;
    };

    /**
     * @description empty session storage
     * @param specific keys array
     * @requires keys in array
     */
    emptySessionStorage = (keys) => {
        return keys.forEach(element => {
            return sessionStorage.removeItem(element);
        });
    };

    getFromSession = (defaultVal, key) => {
        let storage = defaultVal;
        try {
            storage = JSON.parse(sessionStorage.getItem(key));
        } catch (error) {
            // check with this log it
            return defaultVal;
        };
        return storage;
    };

    setSession = (key, value) => {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    redirect = (path) => {
        window.location.href = path;
    };

    getPathname = () => {
        return window.location.pathname;
    };

    getMinutesDiff = (upper, lower) => {
        const diff = moment.duration(upper.diff(lower));
        return parseInt(diff.asMinutes());
    };

    userPermissionRoles = (role) => {
        if (role === Constants.module.ROLE.ADMIN || role === Constants.module.ROLE.CALLCENTER || role === Constants.module.ROLE.SUPERVISOR) {
            return true;
        } else {
            return false;
        }
    };
    setSearchSession(searchIds) {
        const { sessionExpireTimeLimit = 18, EnableSessionTime = false } = configHelper.getConfig();
        if (searchIds.length > 0 && EnableSessionTime) {
            searchIds.forEach((searchId) => {
                const sessionTime = moment().add(sessionExpireTimeLimit, "minutes").format('MM/DD/YYYY HH:mm');
                localStorage.setItem(`lastSearchTime_${searchId}`, sessionTime)
                localStorage.setItem(`lastSearchQuery_${searchId}`, window.location.href);
            });
        }


    };
    removeSearchSession(searchIds) {
        const items = { ...localStorage };
        if (searchIds.length > 0) {
            Object.keys(items).length && Object.keys(items).forEach(item => {
                const lastSearchTime = items[item];
                if (item && (/^lastSearch/.test(item)) && this.getSearchSession(lastSearchTime) < 1) {
                    const [str, id] = item.split("_");
                    if (!searchIds.includes(id)) {
                        localStorage.removeItem(item);
                        localStorage.removeItem(`lastSearchQuery_${id}`);
                    }
                }
            });
        }
    };

    getSearchSession(getLastSearchTime) {
        const start = moment();
        const end = moment(getLastSearchTime);
        const elapsedMinutes = end.diff(start, "minutes");
        return elapsedMinutes;
    };

    createSearchQuery = (query = {}) => {
        const { checkinDate = '', checkoutDate = '', roomInfo = [], searchCriteria = {}, searchPreferences = {} } = query,
            { ratings = [], hid = '' } = searchPreferences,
            { city = '', country = '', cityName = '', nationality = '', countryOfResidence = '' } = searchCriteria;
        let roomInformation = '';
        const crossSell = new URLSearchParams(window.location.search).get('csl') || "";
        roomInfo.forEach(room => {
            const { numberOfAdults = '', numberOfChild = '' } = room;
            roomInformation += `${numberOfAdults}e${numberOfChild}e`;
        });
        return `/hotel/search?city=${cityName}&checkinDate=${checkinDate}&checkoutDate=${checkoutDate}&roomInfo=${roomInformation}&rating=${ratings.join('%2C')}&nationality=${nationality}&countryOfResidence=${countryOfResidence}&fsc=true&cId=${city}&cnId=${country}&hid=${hid}&hideFooter=1${crossSell ? `&csl=${crossSell}` : ''}`;
    };

    daysAddInCheckIn(checkinDate, days) {
        return moment(checkinDate).add(days, 'days').format('YYYY-MM-DD');
    };

    deleteLocalStorage = (key) => {
        localStorage.removeItem(key);
    };

    priceFormat = (value) => {
        return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    };

    currTimeSec = () => {
        return Math.floor(Date.now() / 1000);
    };

    getDomain = () => {
        const hostname = window.location.hostname;
        const { domain = '' } = parseDomain(hostname);
        return domain;
    }

    getTopLevelDomains = () => {
        const hostname = window.location.hostname;
        const { topLevelDomains = [] } = parseDomain(hostname);
        return topLevelDomains;
    };

    isWhitelabel = () => {
        const domain = this.getDomain();
        const clientName = process.env.CLIENT_NAME || 'technogramsolutions';
        const whitelabelFlag = process.env.WHITELABEL_DISABLED || false;
        if (whitelabelFlag) {
            return false;
        } else {
            return clientName ? domain.toLowerCase() !== clientName.toLowerCase() : false;
        }
    };

    getCookieDomain = () => {
        const endingWith = this.getTopLevelDomains().join('.');
        return { cookieDomain: `${this.getDomain()}.${endingWith}` };
    };

    getWhitelabelKey = () => {
        if (this.isWhitelabel()) {
            const { partnerId = '', userId = '' } = this.getUserInfoFromJwt();
            return (partnerId && partnerId != 0) ? partnerId : userId; // type is string
        }
        return '';
    };

    fetchGlobal = () => {
        if (this.isWhitelabel()) {
            const globalKey = window.atob(window.TGSOBJ) || '{}';
            return JSON.parse(globalKey);
        }
        return this.getConfig();
    }

    getFilesPath = (data) => {
        const values = Object.values(data) || {};
        return values.length ? values[0] : '';
    }

    fetchInfoData = () => {
        const globalKey = window.atob(window.TGSINFO) || '{}';
        return JSON.parse(globalKey);
    }

    isB2C = () => {
        const parsedToken = this.getUserInfoFromJwt();
        const { role = '' } = parsedToken || {};
        return (role === 'GUEST' || role === 'CUSTOMER');
    }

    isUser = (checkRole) => {
        const parsedToken = this.getUserInfoFromJwt();
        const { role = '' } = parsedToken || {};
        return role === checkRole;
    }

    getUniqueId = () => {
        return uuidv1();
    }

    getDate = (payload) => {
        return moment(payload).format('D');;
    }

    getMonth = (payload) => {
        return moment(payload).format('MMM');;
    }

    handleFloat = (value, decimal = 2) => {
        const _formatted = parseFloat(parseFloat(value).toFixed(decimal));
        return isNaN(_formatted) ? 0 : _formatted;
    }

    getSpaceForUnderScore = (string = '') => `${string}`.split('_').join(' ');

    // doesUserHavePoints = (pointsType = 'COIN') => {
    //     const parsedToken = this.getUserInfoFromJwt();
    //     const { bs = {} } = parsedToken || {};
    //     const { pointsBalance = {} } = bs;
    //     const { [pointsType]: points = 0 } = pointsBalance;
    //     return points > 0;
    // }

    maskImageUrls = (img = [], sn = " ") => {
        const { pdpImagesSize = [] } = this.getConfig();
        const imgArr = []
        const confImageSizeSupp = ["EXPEDIA", "CLEARTRIP", "AGODA"]
        const pickConfImageSize = pdpImagesSize && confImageSizeSupp.includes(sn)
        img.forEach((imgObj) => {
            const { tns = '', url = '', rids = "", hash_str = '', width = '', height = '', sz = "" } = imgObj;
            const secureTns = tns.replace(/^http:/, 'https:');
            const secureUrl = url.replace(/^http:/, 'https:');
            if (pickConfImageSize) {
                if (pdpImagesSize.includes(sz.toUpperCase()) || sz == "") {
                    imgArr.push({ tns: secureTns, url: secureUrl, rids, hash_str, width, height });
                }
            }
            else {
                imgArr.push({ tns: secureTns, url: secureUrl, rids, hash_str, width, height });
            }

        });
        return imgArr
    }



    asyncSmoothScrollToTop = (top = 0) => {
        window.scrollTo({
            top: top,
            behavior: 'smooth'
        });

        return new Promise((resolve, reject) => {
            const failed = setTimeout(() => {
                reject();
            }, 2000);

            const scrollHandler = () => {
                if (self.pageYOffset === top) {
                    window.removeEventListener("scroll", scrollHandler);
                    clearTimeout(failed);
                    resolve();
                }
            };
            if (self.pageYOffset === top) {
                clearTimeout(failed);
                resolve();
            } else {
                window.addEventListener("scroll", scrollHandler);
            }
        });
    }

    isB2cClient = () => {
        const { clientType = 'b2b' } = configObj;
        return clientType.toUpperCase() === 'B2C';
    }

    updateJWT = (state, updatedUserInfo, jwtToken) => {
        const { userInfo = {} } = state;
        const { ds = {} } = userInfo;
        const { oldDueInfo = [] } = ds;
        const { ds: newRequestDs = {}, ...rest } = updatedUserInfo;
        const data = { ...rest, ds: { ...newRequestDs, oldDueInfo } };
        return { ...data, accessToken: jwtToken };
    }

    getCurrentProduct = () => `HOTEL${this.isB2cClient() ? '_B2C' : ''}${this.getWhitelabelKey() ? ('_' + this.getWhitelabelKey()) : ''}`;

    formatBookingNotes = (list, note) => {
        const separator = ["<strong>", "</strong>"]
        const str = list.reduce((accum, key) => {
            let result = accum;
            let index = result.toLowerCase().indexOf(key.toLowerCase(), 0);
            while (index != -1) {
                result = result.substr(0, index) + `${separator[0]}${result.substr(index, key.length)}${separator[1]}` + result.substr(index + key.length);
                index = result.toLowerCase().indexOf(key.toLowerCase(), index + key.length + separator[0].length);
            }
            return result;
        }, note);
        return this.decodeHTMLEntites(str);
    }

    decodeHTMLEntites = (str) => {
        const map = {
            "&amp;": "&",
            "&lt;": "<",
            "&gt;": ">",
            "&quot;": "\"",
            "&#39;": "'"
        };
        return str.replace(/(&amp;|&lt;|&gt;|&quot;|&#39;)/g, function (m) { return map[m]; });
    }

    removeDuplicateFromArray = (list) => {
        return [...new Set(list)];
    }

    getLocalStorage = (key) => JSON.parse(localStorage.getItem(key));

    setLocalStorage = (key, value) => localStorage.setItem(key, JSON.stringify(value));

    shouldFetch = (time, comparator) => {
        const diff = this.currTimeSec() - time;
        return diff > comparator;
    }

    isJsonString = (str = '') => {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    isPickGallery = (supplierList, thumbnail) => {
        const { imageConfigSearchPage = [] } = this.getConfig();
        return supplierList.reduce((accum, supplier) => accum || imageConfigSearchPage.includes(supplier), false) && !thumbnail;
    }

    isElementInViewPort = (element) => {
        const rect = element.getBoundingClientRect();
        return (
            (rect.top >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight) + 100) ||
            (rect.bottom >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 100)
        );
    }
    getConfig = () => {
        const config = configHelper.getConfig();
        const { applyRoleConfig = false, roleBasedConfig = {}, applyGroupConfig = false, groupBasedConfig = {}, ...restData } = config;
        if (applyRoleConfig || applyGroupConfig) {
            let newConfig = { ...restData };
            const user = this.getUserInfoFromJwt();
            const { role: actualRole = '', additionalInfo = {}, emulateUser = {} } = user,
                { role: emulatedRole = '' } = emulateUser,
                { gps = [] } = additionalInfo;
            const role = _.isEmpty(emulateUser) ? actualRole : emulatedRole;
            if (applyRoleConfig && roleBasedConfig[role]) {  // Applying role config
                newConfig = { ...newConfig, ...roleBasedConfig[role] };
            }
            if (applyGroupConfig) { // Applying Group config
                newConfig = gps.reduce((accum, group) => {
                    if (groupBasedConfig[group]) {
                        return { ...accum, ...groupBasedConfig[group] }
                    } else {
                        return accum;
                    }
                }, newConfig);
            }
            return newConfig;
        }
        return config;
    }
    translateData = (data = [], identifier = '') => {
        return data.map((item) => {
            const { displayLabel = '', id = '', options = [], key = '' } = item;
            let translatedData = {}
            if (!displayLabel && !options.length) {
                return item;
            }
            if (displayLabel) {
                translatedData = {
                    ...item,
                    displayLabel: this.translateText(id || key, identifier, displayLabel)
                }
            }
            if (options.length) {
                translatedData = {
                    ...translatedData,
                    options: this.translateOptions(options, identifier)
                }
            }
            return translatedData;
        })
    }
    translateOptions = (data = [], identifer = '') => {
        return data.map((item) => {
            const { displayLabel = '', id = '', key = '' } = item;
            if (displayLabel) {
                return {
                    ...item,
                    displayLabel: this.translateText(id || key, identifer, displayLabel)
                }
            }
            return item;
        })
    }

    checkPaymentV2 = () => {
        const { paymentV2 = [] } = this.getConfig();
        const parsedToken = this.getUserInfoFromJwt();
        const { userId = '' } = parsedToken || {};
        if ((paymentV2.includes(userId) || paymentV2.includes('All'))) return true;
        return false;
    }

    domParser = (str = '') => {
        const parser = new DOMParser();
        const { body = '' } = parser.parseFromString(str, 'text/html');
        return body.textContent || '';
    }

    checkEmpty = (value) => {
        return value === undefined ||
            value === null ||
            (typeof value === "object" && Object.keys(value).length === 0) ||
            (typeof value === "string" && value.trim().length === 0);
    }
    isRoleCorporateEmp = () => {
        const user = this.getUserInfoFromJwt();
        const { role = '' } = user
        return role == "CORPORATE_EMPLOYEE"
    }

    isRoleCorporate = () => {
        const user = this.getUserInfoFromJwt();
        const { role = '' } = user
        return role == "CORPORATE"
    }

    isRoleCorporateStaff = () => {
        const user = this.getUserInfoFromJwt();
        const { role = '' } = user
        return role == "CORPORATE_STAFF"
    }

    translateText = (key = '', section = '', fallBackText = '') => {
        const ccode = this.getCookie('ccode') || 'IN';
        const locale = this.getCookie('locale') || 'en';
        const countryLocaleTag = `${ccode}_${locale}`;
        const translationKey = `${section}-${key}`
        const finalTranslationKey = `${translationKey}_${countryLocaleTag}`;
        const translationJsonFromLS = this.getLocalStorage(`languageTranslate_${countryLocaleTag}`) || {};
        if (!this.isEmpty(translationJsonFromLS)) {
            return finalTranslationKey in translationJsonFromLS ? translationJsonFromLS[finalTranslationKey] : fallBackText || finalTranslationKey;
        }
        const translationKeyObj = Translations[translationKey] || {};
        return translationKeyObj[countryLocaleTag] || translationKeyObj.IN_en || fallBackText || finalTranslationKey;
    };

    checkPersonalPanNumber = (panNumber = '') => {
        const pan = panNumber ? panNumber.trim() : '';
        return pan.charAt(3) == 'P';
    };

    checkCorporatePanNumber = (panNumber = '') => {
        const pan = panNumber ? panNumber.trim() : '';
        return (pan.charAt(3) == 'C' || pan.charAt(3) == 'F' || pan.charAt(3) == 'H' || pan.charAt(3) == 'T');
    };

    downloadFile = (links, name) => {
        axios({
            url: links,
            method: 'GET',
            responseType: 'blob'
        })
            .then((response) => {
                const url = window.URL
                    .createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
    };

    removeLastRoute = (path = '') => {
        return `/${path.substring(path.lastIndexOf('/') + 1)}` || '/';
    }

    getDefaultRouteForRole = (redirectionConfig = {}, userMenu = []) => {
        const parsedToken = this.getUserInfoFromJwt(),
            { role = 'AGENT' } = parsedToken,
            _redirectPath = redirectionConfig[role] || '',
            _redirectLink = _redirectPath.includes('dashboard') ? this.removeLastRoute(_redirectPath) : '/manage-user';
        return this.isEmpty(userMenu) ? [] :
            userMenu.map((el) => {
                if (el.key == 'dashboard') {
                    el.link = _redirectLink
                }
                return el;
            });
    }

    analysisPublish = (page = '', event = '', data = {}) => {
        const userInfo = this.getUserInfoFromJwt(),
            { analyticsEventEnable = false } = this.getConfig(),
            isEventEnabled = analyticsEventsConfig[page][event];
        const { clevertap = {} } = window;
        if (isEventEnabled && analyticsEventEnable && clevertap && !this.isEmpty(clevertap)) {
            const clevertapCookie = this.getCookie('clevertapId');
            const clevertapId = clevertap.getCleverTapID()
            if (event == 'PushProfile') {
                analysisPublish({
                    product: 'Hotel',
                    page,
                    event,
                    data: {
                        metaData: userInfo,
                        ...data
                    }
                })
                this.setCookie('clevertapId', clevertapId, 34187400000)
            }
            else if ((clevertapId === clevertapCookie) && clevertapCookie !== null) {
                analysisPublish({
                    product: 'Hotel',
                    page,
                    event,
                    data: {
                        metaData: userInfo,
                        ...data
                    }
                })
            }
        }
    }

    getTitleOptions = (pt) => {
        if (pt === 'CHILD') {
            return [{
                display: 'Master',
                val: 'Master'
            },
            {
                display: 'Miss',
                val: 'Miss'
            }];
        }
        return [{
            display: 'Mr',
            val: 'Mr'
        },
        {
            display: 'Mrs',
            val: 'Mrs'
        },
        {
            display: 'Ms',
            val: 'Ms'
        }];
    };

    showPanNumberField = (showPan, isCorporate, onePanReq, isopr, samePanPerRoom, storeKey, paxKey) => {
        return showPan && (!isCorporate && !onePanReq && !isopr && (paxKey == 0 || !samePanPerRoom[storeKey]));
    };
    dateCountryPopupCheck = (countryName, checkinDate) => {
        const { dateCountryPopupData = {} } = this.getConfig();
        const { country = '', checkinDateRange = {} } = dateCountryPopupData;
        const { startDate = '', endDate = '' } = checkinDateRange;
        if ((country.toUpperCase() === countryName.toUpperCase()) && (checkinDate > startDate && checkinDate < endDate)) {
            return true
        }
        return false
    };

    allowedRolesForSupplierSearch = (role) => {
        return (role === Constants.module.ROLE.ADMIN)
    };

    formatDate = (str) => {
        var date = new Date(str),
            mnth = ("0" + (date.getMonth() + 1)).slice(-2),
            day = ("0" + date.getDate()).slice(-2);
        return [day, mnth, date.getFullYear()].join("-");
    };

    getTotalValues = (arr, ss, omi = {}) => {
        const { sgti = {} } = omi;
        const { pah = false, vta = false } = sgti;
        const { allowEditMarkup = false, isB2C, markupAllowConfig = false } = this.getConfig();
        let showMU = false,
            totalMU = 0, totalAmount = 0,
            baseFare = 0, totalMF = 0,
            totalDS = 0, totalVD = 0, totalAmountFare = 0,
            totalAmendmentFee = 0, totalAmendmentFeeTax = 0,
            totalOT = 0,
            totalMFT = 0, totalTax = 0, totalSupplierTax = 0, totalST = 0, totalTSF = 0, totalGT = 0,
            totalTCS = 0;
        let isComputable = vta && !pah;
        let showGT = vta;
        arr.forEach((val) => {
            const { tfcs = {}, tafcs = {} } = val,
                { TAF = {} } = tafcs,
                { MF = 0, MFT = 0, DS = 0, MU = 0, OT = 0, MNT = 0, TSF = 0, ST = 0, CAF = 0, CAFT = 0, HAF = 0, HAFT = 0, VD = 0, TCS = 0, GT = 0 } = TAF,
                { TF = 0, BF = 0, NF = 0 } = tfcs;
            if (MU !== '') {
                totalMU += parseFloat(MU);
            }
            baseFare += parseFloat(BF);
            totalAmountFare += parseFloat(TF);
            totalAmount += parseFloat(TF);
            totalTax += (parseFloat(MF) + parseFloat(MFT) + parseFloat(OT) +
                (parseFloat(MNT) + (MU === '' ? 0 : parseFloat(MU))) +
                parseFloat(CAF) + parseFloat(CAFT) + parseFloat(HAF) +
                parseFloat(HAFT) +
                (isComputable ? parseFloat(GT) : 0));
            totalSupplierTax += parseFloat(TSF) + parseFloat(ST);
            totalST += parseFloat(ST);
            totalTSF += parseFloat(TSF);
            totalMF += parseFloat(MF);
            totalMFT += parseFloat(MFT);
            totalDS += parseFloat(DS);
            totalVD += parseFloat(VD);
            totalAmendmentFee += parseFloat(CAF + HAF);
            totalAmendmentFeeTax += parseFloat(CAFT + HAFT);
            totalOT += parseFloat(OT);
            totalTCS += parseFloat(TCS);
            totalGT += parseFloat(GT);
        });
        if (ss.includes('EXPEDIA') && !markupAllowConfig['EXPEDIA']) {
            allowEditMarkup = false;
        }
        return {
            totalMU, totalAmount, totalAmountFare,
            netfare: totalAmount - totalMU,
            netPrice: totalAmount - totalMU,
            baseFare,
            totalTax,
            totalMF,
            totalMFT,
            totalDS,
            totalVD,
            totalOT,
            allowEditMarkup,
            totalSupplierTax,
            totalST,
            totalTSF,
            totalAmendmentFee,
            totalAmendmentFeeTax,
            totalTCS,
            totalGT: showGT ? totalGT : 0
        };
    }
    makeCall = async ({ request = {}, reply, payload, apiUrl, openApi = false }) => {

        const { headers = {} } = request;
        const { host: hostname = '', authorization: AuthorizationTokan = '' } = headers;
        const cookieToken = request.cookies;
        const { uuid = '', ccode = 'IN', locale = 'en' } = cookieToken;
        const authorization = AuthorizationTokan ? AuthorizationTokan : uuid;
        const { serviceProtocol = 'https' } = whitelabelPreparedData(hostname);
        let Data = {};
        let status = false
        const browser = detect();
        const { name: browserName = '', version = '' } = browser;
        if (!openApi) {
            if (!authorization) return reply.send(sendAccessDeniedResponse());
        }

        await axios({
            method: 'post',
            url: `${serviceProtocol}://${serviceDomain}${apiUrl}`,
            headers: {
                "Content-Type": "application/json",
                Authorization: "",
                "browserName": browserName,
                "browserVersion": version
            },
            data: payload
        }).then(res => {
            const { data = {} } = res;
            const { status: { success = false, httpStatus = '' } } = data;
            if (success) {
                Data = data;
                status = true
            } else {
                status = false
                reply.send(sendAccessDeniedResponse());
            }
        }).catch((err) => {
            status = false
            reply.send(sendAccessDeniedResponse({ error: true }));
        });
        return { Data, status }

    }
    crossSellValidations = (airBookingDetails, reviewJson) => {
        const { itemInfos = {}, order = {} } = airBookingDetails,
            { status = "" } = order,
            { AIR = {} } = itemInfos,
            { travellerInfos: airTravInfo = [], tripInfos = [] } = AIR;
        const { storeJson: hotelTravInfo = [] } = reviewJson;
        const airTravNames = new Set(airTravInfo.map(traveller => `${traveller.fN}${traveller.lN}`.toLowerCase().trim()));
        let hasMatchedNames = false;
        hotelTravInfo.some(room => {
            const { travellerInfo = [] } = room
            return travellerInfo.some(traveller => {
                const { fN = "", lN = "" } = traveller
                const fullName = `${fN}${lN}`;
                hasMatchedNames = airTravNames.has(fullName.toLowerCase().replace(/\s/g, ''));
                return hasMatchedNames;
            });
        });

        return hasMatchedNames && (status == "SUCCESS" || status == "ON_HOLD")
    };


}

const utils = new Utils();

Object.freeze(utils);

export default utils;
